import React, { useEffect, useState } from 'react';
import {
  Typography,
  Box,
  Card,
  CardContent,
  CardHeader,
  TextField,
  Button,
  Avatar,
  Alert,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Menu,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Collapse,
  useTheme,
  useMediaQuery,
  Stack,
  InputAdornment,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import {
  Business,
  Policy,
  Save,
  Clear,
  Code,
  CreditCard,
  Visibility,
  CheckCircle,
  Cancel,
  MoreVert,
  Edit,
  Delete,
  Close,
  Add,
  ViewList,
  ExpandMore,
  ExpandLess,
  Search,
  AddBusiness as AddBusinessIcon,
} from '@mui/icons-material';
import Swal from 'sweetalert2';
import { deleteInsurances, deletePolicy, insuranceRenameImpact, loadInsurance , loadpolicy, NewInsurance, newPolicy, updateInsurance, updatePolicy } from '../BaseURL';
import axios from 'axios';
import { ErrorMessage, successMessage } from '../Message';
import {
  AdminCard, AdminPageHead, AdminSideNav, AdminWorkspace, FormGrid, FormActions,
  ToggleField, PrimaryButton, GhostButton, adminFieldSx, BRAND,
} from './adminUi';


function TabPanel({ children, value, index, ...other }) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: { xs: 2, md: 3 } }}>{children}</Box>}
    </div>
  );
}

// ── Rename impact confirmation ──────────────────────────────────────────────
//
//  Asks the server what a rename would touch, then puts the real numbers in
//  front of the administrator and makes them type the new name to proceed.
//
//  The typing step is deliberate. This is not a dialog anyone should be able
//  to dismiss with a reflexive Enter: a rename rewrites hundreds of thousands
//  of clinical and billing rows, and there is no undo.
const nf = new Intl.NumberFormat();

async function confirmRename(company, newName) {
  // 1. Fetch the impact.
  Swal.fire({
    title: 'Checking impact…',
    text: 'Counting the records that reference this insurance company.',
    allowOutsideClick: false, allowEscapeKey: false, showConfirmButton: false,
    didOpen: () => Swal.showLoading(),
  });

  let impact;
  try {
    const { data } = await axios.get(insuranceRenameImpact(company.id, newName));
    impact = data;
  } catch (err) {
    Swal.close();
    await Swal.fire({
      icon: 'error',
      title: 'Could not check the impact',
      text: err.response?.data?.message
        || 'The server could not report what this rename would affect. The rename has not been made.',
      confirmButtonColor: BRAND.danger,
    });
    return false;
  }
  Swal.close();

  if (!impact?.success) {
    await Swal.fire({ icon: 'error', title: 'Could not check the impact', text: impact?.message || 'Unknown error.' });
    return false;
  }

  // 2. Refuse a rename onto a name already in use — it would merge two
  //    insurers' records with no way back.
  if (impact.nameTaken) {
    await Swal.fire({
      icon: 'error',
      title: 'That name is already taken',
      html: `Another insurance company is already called <strong>${newName}</strong>.<br/><br/>`
        + 'Renaming onto an existing name would merge both companies&rsquo; records permanently. Choose a different name.',
      confirmButtonColor: BRAND.danger,
    });
    return false;
  }

  const cat  = impact.catalogue  || { total: 0, tables: [] };
  const hist = impact.historical || { total: 0, tables: [] };
  const pat  = impact.patients   || { total: 0, tables: [] };

  const line = (t, accent) => `
    <tr>
      <td style="padding:6px 10px;border-bottom:1px solid #eef2f7;text-align:left;font-size:12.5px;color:#0F1F3D">
        ${t.label}
        <div style="font-size:10.5px;color:#93A2B8;margin-top:1px">${t.table}.${t.column}</div>
      </td>
      <td style="padding:6px 10px;border-bottom:1px solid #eef2f7;text-align:right;font-size:13px;font-weight:800;color:${accent};white-space:nowrap">
        ${nf.format(t.rows)}
      </td>
    </tr>`;

  const catRows  = cat.tables.map((t) => line(t, BRAND.greenDark)).join('');
  const histRows = hist.tables.map((t) => line(t, '#93A2B8')).join('');

  const { isConfirmed, value } = await Swal.fire({
    icon: undefined,
    title: 'Rename this insurance company?',
    width: 680,
    html: `
      <div style="text-align:left">
        <div style="display:flex;align-items:center;gap:10px;justify-content:center;margin:4px 0 18px;flex-wrap:wrap">
          <span style="background:#F4F7FB;border:1px solid #DDE5EF;border-radius:8px;padding:6px 12px;font-weight:700;color:#5A6B87">${impact.oldName}</span>
          <span style="color:#93A2B8;font-size:18px">&rarr;</span>
          <span style="background:${BRAND.greenSoft};border:1px solid ${BRAND.green}55;border-radius:8px;padding:6px 12px;font-weight:800;color:${BRAND.greenDark}">${newName}</span>
        </div>

        <!-- what changes -->
        <div style="display:flex;align-items:center;gap:8px;margin-bottom:8px">
          <span style="width:8px;height:8px;border-radius:50%;background:${BRAND.green};display:inline-block"></span>
          <strong style="font-size:12.5px;color:${BRAND.greenDark};letter-spacing:.4px">
            WILL BE RENAMED &mdash; ${nf.format(cat.total)} record${cat.total === 1 ? '' : 's'}
          </strong>
        </div>
        <p style="font-size:12.5px;color:#5A6B87;line-height:1.65;margin:0 0 8px">
          Tariffs and price lists are looked up <em>by name</em>. If they kept the old
          spelling, no price would resolve for this scheme and staff could not raise
          new requests against it.
        </p>
        <table style="width:100%;border-collapse:collapse;border:1px solid #eef2f7;border-radius:8px;overflow:hidden">
          ${catRows || '<tr><td style="padding:10px;color:#93A2B8;font-size:12.5px">Only the company record itself.</td></tr>'}
        </table>

        <!-- what is preserved -->
        <div style="display:flex;align-items:center;gap:8px;margin:18px 0 8px">
          <span style="width:8px;height:8px;border-radius:50%;background:#93A2B8;display:inline-block"></span>
          <strong style="font-size:12.5px;color:#5A6B87;letter-spacing:.4px">
            LEFT UNCHANGED &mdash; ${nf.format(hist.total)} historical record${hist.total === 1 ? '' : 's'}
          </strong>
        </div>
        <p style="font-size:12.5px;color:#5A6B87;line-height:1.65;margin:0 0 8px">
          Prescriptions, claims, visits and charges keep the name they were created
          under. A claim submitted to the insurer last month was submitted under the
          old name, and the archive must continue to say so.
        </p>
        <table style="width:100%;border-collapse:collapse;border:1px solid #eef2f7;border-radius:8px;overflow:hidden;opacity:.75">
          ${histRows || '<tr><td style="padding:10px;color:#93A2B8;font-size:12.5px">No historical records.</td></tr>'}
        </table>

        ${pat.total > 0 ? `
        <label style="display:flex;gap:10px;align-items:flex-start;margin-top:16px;padding:10px 13px;background:#F4F7FB;border:1px solid #DDE5EF;border-radius:9px;cursor:pointer">
          <input type="checkbox" id="rename-include-patients" style="margin-top:2px;width:16px;height:16px;accent-color:${BRAND.green}" />
          <span style="font-size:12.5px;color:#0F1F3D;line-height:1.6">
            <strong>Also update ${nf.format(pat.total)} patient registration${pat.total === 1 ? '' : 's'}</strong>
            <span style="display:block;color:#5A6B87;margin-top:2px">
              Their <em>current</em> scheme, not their visit history. Leave this off and those
              patients keep a scheme name that no longer matches any registered company,
              which may stop their next visit from pricing.
            </span>
          </span>
        </label>` : ''}

        <div style="background:#FFF8EC;border:1px solid #E08B2F55;border-radius:9px;padding:10px 13px;margin-top:14px">
          <div style="font-size:12.5px;color:#8A5A12;line-height:1.65">
            <strong>This cannot be undone.</strong> All of it succeeds or none of it does &mdash;
            there is no half-renamed state &mdash; but there is no automatic way back
            afterwards. Take a database backup first if you are unsure.
          </div>
        </div>

        <p style="font-size:12.5px;color:#5A6B87;margin:14px 0 6px">
          Type <strong>${newName}</strong> below to confirm:
        </p>
      </div>`,
    input: 'text',
    inputPlaceholder: newName,
    inputAttributes: { autocapitalize: 'off', autocorrect: 'off', autocomplete: 'off' },
    showCancelButton: true,
    confirmButtonText: `Rename ${nf.format(cat.total)} catalogue record${cat.total === 1 ? '' : 's'}`,
    cancelButtonText: 'Cancel',
    confirmButtonColor: BRAND.green,
    cancelButtonColor: '#5A6B87',
    reverseButtons: true,
    focusCancel: true,
    allowOutsideClick: false,
    // Read the checkbox before SweetAlert tears the DOM down.
    preConfirm: () => ({
      includePatients: !!document.getElementById('rename-include-patients')?.checked,
    }),
    // Case-insensitive: the point is to make the operator read the name, not
    // to test their shift key.
    inputValidator: (v) =>
      (v || '').trim().toLowerCase() === newName.toLowerCase()
        ? undefined
        : 'Type the new name exactly to confirm',
  });

  if (!isConfirmed) return false;
  return { includePatients: !!value?.includePatients };
}

// Section navigation
//
// A vertical list down the left rather than a horizontal tab strip. Four
// sections could not describe themselves on one line of width — which is why
// the old strip abbreviated to "Add Co." — and there was no room for the
// counts. Down the side each entry carries its name, what it is for, and how
// many records it holds. AdminSideNav collapses it to a chip row below `md`.
const ResponsiveNavigation = ({
  tabValue, handleTabChange, editingCompany, editingPolicy,
  companies = [], policies = [],
}) => {
  const items = [
    {
      icon: AddBusinessIcon,
      label: editingCompany ? 'Edit Company' : 'Register Company',
      shortLabel: editingCompany ? 'Edit Co.' : 'Add Co.',
      caption: editingCompany ? `Editing ${editingCompany.name || 'company'}` : 'Add a new insurer',
    },
    {
      icon: Business,
      label: 'Companies List',
      shortLabel: 'Companies',
      caption: 'Registered insurers',
      badge: companies.length,
    },
    {
      icon: ViewList,
      label: 'Policies List',
      shortLabel: 'Policies',
      caption: 'Schemes across all insurers',
      badge: policies.length,
    },
    {
      icon: Policy,
      label: editingPolicy ? 'Edit Policy' : 'Register Policy',
      shortLabel: editingPolicy ? 'Edit Pol.' : 'Add Pol.',
      caption: editingPolicy ? 'Editing an existing scheme' : 'Add a scheme to an insurer',
    },
  ];

  // Derived from the loaded records, so the figures cannot drift from the
  // lists they summarise. `ccc` and `support` are stored inconsistently across
  // older rows — as 'Required'/'1' and 1/'1' — so both spellings are counted.
  const requiresClaimCode = companies.filter(
    (c) => c.ccc === 'Required' || c.ccc === '1' || c.ccc === 1
  ).length;
  const dualInsurance = companies.filter(
    (c) => c.support === 1 || c.support === '1'
  ).length;

  const stats = [
    { label: 'Companies',   value: companies.length },
    { label: 'Policies',    value: policies.length },
    { label: 'Claim code',  value: requiresClaimCode, accent: BRAND.green },
    { label: 'Dual cover',  value: dualInsurance,     accent: BRAND.green },
  ];

  return (
    <AdminSideNav
      value={tabValue}
      onChange={handleTabChange}
      items={items}
      stats={stats}
    />
  );
};

// Insurance Company Form Component
const InsuranceCompanyForm = ({ onSubmit, editingCompany, onCancel }) => {

  // A scheme with no fixed membership-number length stores 0. Anything that is
  // not a positive count means the same thing — including the NULL and '' left
  // by schemes set up before this option existed — so they all present as
  // "unspecified" rather than as a half-filled form.
  const anyLengthFrom = (company) => !(Number(company?.member_count) > 0);

  const blankForm = () => ({
    insuranceName: '',
    isClaimCheckRequired: false,
    showNonInsuranceMedication: false,
    visitPerMonth: '',
    claimCheckCodeCount: '',
    membershipNoCount: '',
    cardSerialNoCount: '',
    cardTypes: '',
    supportDualInsurance: false,
    membershipNoAnyLength: false,
  });

  const formFrom = (company) => (company ? {
    insuranceName: company.name || '',
    isClaimCheckRequired: company.ccc === 'Required' || company.ccc === '1',
    showNonInsuranceMedication: company.med === 'yes' || company.med === '1',
    visitPerMonth: company.Vsit || '',
    claimCheckCodeCount: company.cccc || '',
    // Held as '0' rather than blank while the box is ticked, so the value
    // posted to the server is the sentinel itself and not an empty string.
    membershipNoCount: anyLengthFrom(company) ? '0' : company.member_count,
    membershipNoAnyLength: anyLengthFrom(company),
    cardSerialNoCount: company.Seria_count || '',
    cardTypes: company.Seria || '',
    supportDualInsurance: company.support === 1,
  } : blankForm());

  // Initialize form data with proper mapping from API response
  const [formData, setFormData] = useState(() => formFrom(editingCompany));

  const [errors, setErrors] = useState({});
 
  // Update form data when editingCompany changes
  useEffect(() => {
    setFormData(formFrom(editingCompany));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editingCompany]);
 
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));

    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: '',
      }));
    }
  };

  // The checkbox and the number field are two views of one stored value, so
  // they are written together. Ticking parks the sentinel in membershipNoCount
  // (nothing downstream needs to know about the boolean); unticking clears it,
  // which leaves the admin in front of an empty field to type the real count
  // into rather than a stale 0 they have to delete first.
  const handleMembershipAnyLengthChange = (e) => {
    const on = e.target.checked;
    setFormData(prev => ({
      ...prev,
      membershipNoAnyLength: on,
      membershipNoCount: on ? '0' : '',
    }));
    setErrors(prev => ({ ...prev, membershipNoCount: '' }));
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.insuranceName.trim()) {
      newErrors.insuranceName = 'Insurance name is required';
    }

    if (!formData.visitPerMonth || formData.visitPerMonth <= 0) {
      newErrors.visitPerMonth = 'Please enter a valid number of visits per month';
    }

    // Only meaningful when a length is actually being declared. Ticking
    // "unspecified" is the supported way to say "no fixed length" — a typed 0
    // or a negative number is someone trying to say it the wrong way.
    if (!formData.membershipNoAnyLength && String(formData.membershipNoCount).trim() !== '') {
      if (!(Number(formData.membershipNoCount) > 0)) {
        newErrors.membershipNoCount =
          'Enter a length of 1 or more, or tick "Unspecified" to accept any length';
      }
    }

    if (!formData.cardTypes.trim()) {
      newErrors.cardTypes = 'Please enter at least one card type';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  

 const handleSubmit = async(e) => {
  e.preventDefault();

  if (validateForm()) {
    // ── Renaming? Show what it will touch, first. ──────────────────────────
    //
    // The company name is stored as a STRING across ~27 columns rather than
    // referenced by id, so a rename rewrites hundreds of thousands of rows —
    // tariffs, claims, prescriptions, patient registrations. That is not
    // something to discover afterwards, so we ask the server for the exact
    // counts and put them in front of the administrator before committing.
    const isRename =
      !!editingCompany &&
      formData.insuranceName.trim() &&
      formData.insuranceName.trim() !== (editingCompany.name || '').trim();

    let renameOptions = null;
    if (isRename) {
      renameOptions = await confirmRename(editingCompany, formData.insuranceName.trim());
      if (!renameOptions) return;
    }

     const result = isRename ? { isConfirmed: true } : await Swal.fire({
      title: 'Confirm / Cancel',
      text: editingCompany ? "Are You Sure You Want To Update Insurance Company ?" : "Are You Sure You Want To Register Insurance Company ?",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Yes Confirm'
  });

  if (result.isConfirmed) {
      Swal.fire({
      title: 'Processing.......',
      text: 'Please wait',
      allowOutsideClick: false,
      allowEscapeKey: false,
      showConfirmButton: false,
      didOpen: () => {
      Swal.showLoading();
      }
      }); 
      
      try {
        if (editingCompany) {
          // Update existing company
         
          const updateData = {
            ...formData,
            id: editingCompany.id,
            // Only meaningful on a rename; the server ignores it otherwise.
            includePatients: renameOptions?.includePatients === true,
          };
          
         // console.log(updateData)

          
           const response = await axios.put(updateInsurance(updateData.id), updateData);
                      
              if (response.data.success) {
      
              successMessage('Save',response.data.message)
              
              // Call onSubmit with true to trigger refresh
              onSubmit(true);
              }

        } else {
          // Create new company
          const response = await axios.post(NewInsurance, formData);
                      
          if (response.data.success) {
            successMessage('Save', response.data.message);
            
            // Call onSubmit with true to trigger refresh
            onSubmit(true);
          }
        }

      
       handleReset();

      } catch (error) {
        await Swal.fire({
          title: 'Error',
          text: error.response?.data?.message || 'Failed to Save',
          icon: 'error'
        });
      }

    }
  }
};

  const handleReset = () => {
    setFormData(blankForm());
    setErrors({});
  };

  const cardTypesArray = typeof formData.cardTypes === 'string' 
    ? formData.cardTypes.split(',').map(type => type.trim()).filter(type => type)
    : formData.cardTypes || [];

  // Every field on this form carries the same props, and repeating them nine
  // times is how a form ends up with one input a different size than the rest.
  const field = (name, label, extra = {}) => ({
    fullWidth: true,
    name,
    label,
    value: formData[name],
    onChange: handleInputChange,
    error: !!errors[name],
    helperText: errors[name] || extra.helperText || ' ',
    size: 'small',
    sx: adminFieldSx,
    ...extra,
  });

  return (
    <AdminCard
      icon={Business}
      title={editingCompany ? 'Update Insurance Company' : 'Register Insurance Company'}
      subtitle={
        editingCompany
          ? 'Modify company information'
          : 'Enter all required information to register a new insurance company'
      }
      action={
        editingCompany && onCancel && (
          <IconButton onClick={onCancel} size="small" aria-label="Cancel edit">
            <Close sx={{ fontSize: 19 }} />
          </IconButton>
        )
      }
    >
      <form onSubmit={handleSubmit} noValidate>
        {/* Two columns from tablet, three from large. The old markup used
            MUI's <Grid item xs md> — props MUI 7 ignores outright — so the
            column behaviour here was whatever `size={4}` happened to give. */}
        <FormGrid columns={{ sm: 2, lg: 3 }}>
          <TextField {...field('insuranceName', 'Insurance Company Name', { required: true })} />
          <TextField {...field('claimCheckCodeCount', 'Claim Check Code Count', { type: 'number' })} />

          <ToggleField
            name="isClaimCheckRequired"
            checked={formData.isClaimCheckRequired}
            onChange={handleInputChange}
            label="Claim Check Code Required"
            description="Staff must enter a claim check code for every visit"
          />

          <TextField {...field('visitPerMonth', 'Visits Per Month', { type: 'number', required: true })} />

          {/* Membership Number Count, with an escape hatch.
              Not every scheme issues fixed-length membership numbers, and for
              those there is no count to enter — leaving the box empty used to
              be the only way to say so, which reads as "not filled in yet"
              rather than "this scheme has no fixed length". Ticking the box
              states it explicitly and stores 0, the sentinel the registration
              screens read to skip the length check. */}
          <Box sx={{ minWidth: 0 }}>
            <TextField
              {...field('membershipNoCount', 'Membership Number Count', {
                type: 'number',
                disabled: formData.membershipNoAnyLength,
                value: formData.membershipNoAnyLength ? '' : formData.membershipNoCount,
                placeholder: formData.membershipNoAnyLength ? 'Any length' : undefined,
                inputProps: { min: 1 },
                helperText: errors.membershipNoCount
                  || (formData.membershipNoAnyLength
                        ? 'Any membership number will be accepted'
                        : 'Exact number of characters on the card'),
              })}
            />
            <FormControlLabel
              sx={{ mt: -.75, ml: .25 }}
              control={
                <Checkbox
                  size="small"
                  name="membershipNoAnyLength"
                  checked={formData.membershipNoAnyLength}
                  onChange={handleMembershipAnyLengthChange}
                  sx={{ color: BRAND.inkFaint, '&.Mui-checked': { color: BRAND.green } }}
                />
              }
              label={
                <Typography sx={{ fontSize: 12.5, color: BRAND.inkMuted, fontWeight: 600 }}>
                  Unspecified — accept any length
                </Typography>
              }
            />
          </Box>

          <ToggleField
            name="showNonInsuranceMedication"
            checked={formData.showNonInsuranceMedication}
            onChange={handleInputChange}
            label="Show Non Insurance Medication"
            description="Include medicines this scheme does not cover"
          />

          <TextField {...field('cardSerialNoCount', 'Card Serial Number Count', { type: 'number' })} />

          <Box sx={{ minWidth: 0 }}>
            <TextField
              {...field('cardTypes', 'Card Types (separate with commas)', {
                required: true,
                placeholder: 'Premium, Standard, Basic',
                helperText: errors.cardTypes || 'e.g., Premium, Standard, Basic',
                value: Array.isArray(formData.cardTypes) ? formData.cardTypes.join(', ') : formData.cardTypes,
              })}
            />
            {/* Echoes back what the comma-separated string actually parsed
                into — the one place this form can be silently misread. */}
            {cardTypesArray.length > 0 && (
              <Box sx={{ mt: .5, display: 'flex', flexWrap: 'wrap', gap: .75 }}>
                {cardTypesArray.map((type, index) => (
                  <Chip
                    key={index}
                    label={type}
                    size="small"
                    sx={{
                      height: 22, fontSize: 11.5, fontWeight: 600,
                      bgcolor: BRAND.navySoft, color: BRAND.navy,
                    }}
                  />
                ))}
              </Box>
            )}
          </Box>

          <ToggleField
            name="supportDualInsurance"
            checked={formData.supportDualInsurance}
            onChange={handleInputChange}
            label="Support Dual Insurance"
            description="Patients may hold a second scheme alongside this one"
          />
        </FormGrid>

        <FormActions>
          {editingCompany && onCancel && (
            <GhostButton startIcon={<Close />} onClick={onCancel}>Cancel</GhostButton>
          )}
          <GhostButton startIcon={<Clear />} onClick={handleReset}>Clear Form</GhostButton>
          <PrimaryButton type="submit" startIcon={<Save />}>
            {editingCompany ? 'Update Company' : 'Register Company'}
          </PrimaryButton>
        </FormActions>
      </form>
    </AdminCard>
  );
};

// Company Actions Menu Component
const CompanyActionsMenu = ({ company, onEdit, onDelete, onViewPolicies }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  
  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };
  
  const handleClose = () => {
    setAnchorEl(null);
  };

  return (
    <>
      <IconButton onClick={handleClick} size="small">
        <MoreVert />
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
      >
        <MenuItem onClick={() => { onEdit(company); handleClose(); }}>
          <Edit sx={{ mr: 1 }} /> Edit Company
        </MenuItem>
        <MenuItem onClick={() => { onViewPolicies(company); handleClose(); }}>
          <ViewList sx={{ mr: 1 }} /> View Policies
        </MenuItem>
        <MenuItem onClick={() => { onDelete(company); handleClose(); }} sx={{ color: 'error.main' }}>
          <Delete sx={{ mr: 1 }} /> Delete Company
        </MenuItem>
      </Menu>
    </>
  );
};

// Responsive Company Card Component
const ResponsiveCompanyCard = ({ company, onEdit, onDelete, onViewPolicies, onCompanySelect }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [expanded, setExpanded] = useState(false);

  return (
    <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <CardHeader
        title={
          <Typography variant={isMobile ? "body1" : "h6"} noWrap>
            {company.name}
          </Typography>
        }
        subheader={
          <Typography variant="caption" color="text.secondary">
            Code: {company.id}
          </Typography>
        }
        action={
          <CompanyActionsMenu
            company={company}
            onEdit={onEdit}
            onDelete={onDelete}
            onViewPolicies={onViewPolicies}
          />
        }
      />
      
      <CardContent sx={{ flexGrow: 1, pt: 0 }}>
        {isMobile ? (
          // Mobile collapsed view
          <>
            <Button
              fullWidth
              variant="text"
              onClick={() => setExpanded(!expanded)}
              endIcon={expanded ? <ExpandLess /> : <ExpandMore />}
              sx={{ mb: 1 }}
            >
              {expanded ? 'Show Less' : 'Show Details'}
            </Button>
            <Collapse in={expanded}>
              <List dense>
                <ListItem sx={{ px: 0 }}>
                  <ListItemAvatar>
                    <Avatar sx={{ bgcolor: 'secondary.main', width: 32, height: 32 }}>
                      <Code fontSize="small" />
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary="Claim Check"
                    secondary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {company.ccc=='Required' || company.ccc==1  ? (
                          <CheckCircle color="success" fontSize="small" />
                        ) : (
                          <Cancel color="error" fontSize="small" />
                        )}
                        <Typography variant="body2">
                          {company.ccc }
                        </Typography>
                      </Box>
                    }
                  />
                </ListItem>

                <ListItem sx={{ px: 0 }}>
                  <ListItemAvatar>
                    <Avatar sx={{ bgcolor: 'accent.main', width: 32, height: 32 }}>
                      <Visibility fontSize="small" />
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary="Visits/Month"
                    secondary={`${company.Vsit} visits`}
                  />
                </ListItem>

                <ListItem sx={{ px: 0 }}>
                  <ListItemAvatar>
                    <Avatar sx={{ bgcolor: 'info.main', width: 32, height: 32 }}>
                      <CreditCard fontSize="small" />
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary="Card Types"
                    secondary={
                      <Box sx={{ mt: 0.5, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {company.Seria}
                      </Box>
                    }
                  />
                </ListItem>
              </List>
            </Collapse>
          </>
        ) : (
          // Desktop full view
         <List dense>
            <ListItem>
              <ListItemAvatar>
                <Avatar sx={{ bgcolor: 'secondary.main', width: 32, height: 32 }}>
                  <Code fontSize="small" />
                </Avatar>
              </ListItemAvatar>
              <ListItemText
                primary="Claim Check"
                secondary={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {company.ccc=='Required' || company.ccc==1 ? (
                      <CheckCircle color="success" fontSize="small" />
                    ) : (
                      <Cancel color="error" fontSize="small" />
                    )}
                    <Typography variant="body2">
                      {company.ccc}
                    </Typography>
                  </Box>
                }
              />
            </ListItem>

            <ListItem>
              <ListItemAvatar>
                <Avatar sx={{ bgcolor: 'accent.main', width: 32, height: 32 }}>
                  <Visibility fontSize="small" />
                </Avatar>
              </ListItemAvatar>
              <ListItemText
                primary="Visits/Month"
                secondary={`${company.Vsit} visits`}
              />
            </ListItem>

            <ListItem>
              <ListItemAvatar>
                <Avatar sx={{ bgcolor: 'info.main', width: 32, height: 32 }}>
                  <CreditCard fontSize="small" />
                </Avatar>
              </ListItemAvatar>
              <ListItemText
                primary="Card Types"
                secondary={
                  <Box sx={{ mt: 0.5, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                     {company.Seria}
                  </Box>
                }
              />
            </ListItem>
          </List>
        )}


        {/* Two even halves. <Grid item xs={6}> did nothing under MUI 7, so
            these two facts were stacking instead of sitting side by side. */}
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0,1fr))', gap: 1 }}>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="caption" color="text.secondary">
              Dual Insurance
            </Typography>
            <Typography variant="body2" fontSize={{ xs: '0.75rem', sm: '0.875rem' }}>
              {company.support ? 'Supported' : 'Not Supported'}
            </Typography>
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="caption" color="text.secondary">
              Show Non-Insurance Medication
            </Typography>
            <Typography variant="body2" fontSize={{ xs: '0.75rem', sm: '0.875rem' }}>
              {company.med === 'yes' ? 'Yes' : 'No'}
            </Typography>
          </Box>
        </Box>

        <Box sx={{ mt: 2 }}>
          <Typography variant="caption" color="text.secondary">
            Registered: {new Date(company.registeredAt).toLocaleDateString()}
          </Typography>
        </Box>
      </CardContent>

      <Box sx={{ p: 2, pt: 0 }}>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
          <Button
            fullWidth
            variant="outlined"
            onClick={() => onEdit(company)}
            startIcon={<Edit />}
            size="small"
          >
            Edit
          </Button>
          <Button
            fullWidth
            variant="contained"
            onClick={() => onCompanySelect(company)}
            startIcon={<Add />}
            size="small"
          >
            Policy
          </Button>
        </Stack>
      </Box>
    </Card>
  );
};

// Insurance Company List Component with Search
const InsuranceCompanyList = ({ companies, onCompanySelect, onEdit, onDelete, onViewPolicies }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Filter companies based on search term
  const filteredCompanies = companies.filter(company =>
    company.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    company.id.toString().includes(searchTerm) ||
    company.Seria.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (companies.length === 0) {
    return (
      <Alert severity="info">
        No insurance companies registered yet. Use the "Register Company" tab to add your first company.
      </Alert>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }}>
        <Typography variant="h5" sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>
          Registered Insurance Companies ({companies.length})
        </Typography>
        
        <TextField
          size={isMobile ? "small" : "medium"}
          placeholder="Search companies..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          sx={{ minWidth: { xs: '100%', sm: 250 } }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search />
              </InputAdornment>
            ),
          }}
        />
      </Box>

      {filteredCompanies.length === 0 ? (
        <Alert severity="info">
          No companies found matching "{searchTerm}". Try adjusting your search terms.
        </Alert>
      ) : (
        // CSS Grid, not <Grid item xs sm lg> — MUI 7 ignores those props, so
        // every card was falling back to content width and the rows came out
        // ragged. auto-fill also means the row fills whatever width the
        // workspace gives it instead of being pinned to three columns.
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              sm: 'repeat(auto-fill, minmax(300px, 1fr))',
            },
            gap: { xs: 2, sm: 3 },
            alignItems: 'start',
          }}
        >
          {filteredCompanies.map((company) => (
            <ResponsiveCompanyCard
              key={company.id}
              company={company}
              onEdit={onEdit}
              onDelete={onDelete}
              onViewPolicies={onViewPolicies}
              onCompanySelect={onCompanySelect}
            />
          ))}
        </Box>
      )}
    </Box>
  );
};

// Insurance Policy Form Component - UPDATED VERSION WITH RELOAD
const InsurancePolicyForm = ({ companies, selectedCompany, onSubmit, editingPolicy, onCancel }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  // Initialize form data properly for both new and editing modes
  const [formData, setFormData] = useState(() => {
    if (editingPolicy) {
      // Find company by name if companyId is not available
      let companyId = editingPolicy.companyId || editingPolicy.company_id || '';
      if (!companyId && editingPolicy.insurance) {
        const foundCompany = companies.find(c => c.name === editingPolicy.insurance);
        companyId = foundCompany ? foundCompany.id : '';
      }
      
      return {
        companyId: companyId,
        policyName: editingPolicy.policy || editingPolicy.policyName || ''
      };
    }
    return {
      companyId: selectedCompany?.id || '',
      policyName: ''
    };
  });

  const [errors, setErrors] = useState({});

  // Update form data when editingPolicy or companies change
  useEffect(() => {
    if (editingPolicy) {
      // Find company by name if companyId is not available
      let companyId = editingPolicy.companyId || editingPolicy.company_id || '';
      if (!companyId && editingPolicy.insurance && companies.length > 0) {
        const foundCompany = companies.find(c => c.name === editingPolicy.insurance);
        companyId = foundCompany ? foundCompany.id : '';
      }
      
      setFormData({
        companyId: companyId,
        policyName: editingPolicy.policy || editingPolicy.policyName || ''
      });
    } else {
      setFormData({
        companyId: selectedCompany?.id || '',
        policyName: ''
      });
    }
  }, [editingPolicy, selectedCompany, companies]);
 
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
    
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: '',
      }));
    }
  };

  
  const validateForm = () => {
    const newErrors = {};

    if (!formData.companyId) {
      newErrors.companyId = 'Please select an insurance company';
    }

    if (!formData.policyName.trim()) {
      newErrors.policyName = 'Policy name is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async(e) => {
    e.preventDefault();

    if (validateForm()) {
      const selectedCompanyData = companies.find(c => c.id.toString() === formData.companyId.toString());
      
      const processedData = {
        ...formData,
        companyName: selectedCompanyData?.name || '',
        // Include the ID for editing
        ...(editingPolicy && { id: editingPolicy.id })
      };

      const result = await Swal.fire({
        title: 'Confirm / Cancel',
        text: editingPolicy 
          ? "Are You Sure You Want To Update Insurance Policy ?" 
          : "Are You Sure You Want To Save Insurance Policy Information ?",
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#3085d6',
        cancelButtonColor: '#d33',
        confirmButtonText: 'Yes Confirm'
      });

      if (result.isConfirmed) {
        Swal.fire({
          title: 'Processing.......',
          text: 'Please wait',
          allowOutsideClick: false,
          allowEscapeKey: false,
          showConfirmButton: false,
          didOpen: () => {
            Swal.showLoading();
          }
        }); 

        try {
          if (editingPolicy) {
            const response = await axios.put(updatePolicy(editingPolicy.id), processedData);

            if (response.data.success) {
              successMessage('Update', response.data.message);
              // Call onSubmit with true to trigger refresh
              onSubmit(true);
              handleReset();
            }
            
          } else {
            // Create new policy
            const response = await axios.post(newPolicy, processedData);
                        
            if (response.data.success) {
              successMessage('Save', response.data.message);
              // Call onSubmit with true to trigger refresh
              onSubmit(true);
              handleReset();
            }
          }

        } catch (error) {
          await Swal.fire({
            title: 'Error',
            text: error.response?.data?.message || 'Failed to Save',
            icon: 'error'
          });
        }
      }
    }
  };

  const handleReset = () => {
    setFormData({
      companyId: selectedCompany?.id || '',
      policyName: '',
    });
    setErrors({});
  };

  if (companies.length === 0) {
    return (
      <Alert severity="warning">
        No insurance companies available. Please register at least one insurance company first.
      </Alert>
    );
  }

  // FIX: Get the selected company data based on current form companyId
  const selectedCompanyData = companies.find(c => c.id.toString() === formData.companyId.toString());
  
  // FIX: For editing mode, if we have editingPolicy but no selectedCompanyData, 
  // try to find it using the insurance name from the policy
  let displayCompanyData = selectedCompanyData;
  if (editingPolicy && !selectedCompanyData && editingPolicy.insurance) {
    displayCompanyData = companies.find(c => c.name === editingPolicy.insurance);
    // Update the form data with the found company ID
    if (displayCompanyData && formData.companyId !== displayCompanyData.id.toString()) {
      setFormData(prev => ({...prev, companyId: displayCompanyData.id.toString()}));
    }
  }

  return (
    <Card>
      <CardHeader
        avatar={<Policy color="primary" />}
        title={
          <Typography variant={isMobile ? "h6" : "h5"}>
            {editingPolicy ? "Update Insurance Policy" : "Register Insurance Policy"}
          </Typography>
        }
        subheader={editingPolicy ? "Modify policy information" : "Create a new insurance policy for a registered company"}
        action={
          editingPolicy && onCancel && (
            <IconButton onClick={onCancel}>
              <Close />
            </IconButton>
          )
        }
      />
      <CardContent>

        {/* FIX: Display company info using displayCompanyData */}
        {displayCompanyData && (
          <Box sx={{ mb: 3, p: 2, bgcolor: 'primary.50', borderRadius: 2, border: '1px solid', borderColor: 'primary.200' }}>
            <Typography variant="subtitle1" color="primary">
              {editingPolicy ? 'Current Company: ' : 'Selected Company: '}{displayCompanyData.name}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Code: {displayCompanyData.id} | Max Visits/Month: {displayCompanyData.Vsit}
            </Typography>
            <Box sx={{ mt: 1 }}>
              {displayCompanyData.Seria}
            </Box>
          </Box>
        )}

        {/* FormGrid/FormActions, matching the company form. The old <Grid
            container columns={...}> mixed the v1 `xs/md` props with the v2
            `size` prop; MUI 7 honours neither combination here, so both
            fields rendered full width regardless. */}
        <form onSubmit={handleSubmit}>
          <FormGrid columns={{ sm: 2 }}>
            <FormControl fullWidth error={!!errors.companyId} sx={adminFieldSx}>
              <InputLabel>Insurance Company</InputLabel>
              <Select
                name="companyId"
                value={formData.companyId}
                onChange={handleInputChange}
                label="Insurance Company"
                required
              >
                {companies.map((company) => (
                  <MenuItem key={company.id} value={company.id}>
                    {company.name} ({company.id})
                  </MenuItem>
                ))}
              </Select>
              {errors.companyId && (
                <Typography variant="caption" color="error" sx={{ mt: .5, ml: 1.75 }}>
                  {errors.companyId}
                </Typography>
              )}
            </FormControl>

            <TextField
              fullWidth
              label="Policy Name"
              name="policyName"
              value={formData.policyName}
              onChange={handleInputChange}
              error={!!errors.policyName}
              helperText={errors.policyName || 'The scheme name as the insurer publishes it'}
              required
              sx={adminFieldSx}
            />
          </FormGrid>

          <FormActions>
            {editingPolicy && onCancel && (
              <GhostButton startIcon={<Close />} onClick={onCancel}>
                Cancel
              </GhostButton>
            )}
            <GhostButton startIcon={<Clear />} onClick={handleReset}>
              Clear Form
            </GhostButton>
            <PrimaryButton type="submit" startIcon={<Save />}>
              {editingPolicy ? 'Update Policy' : 'Register Policy'}
            </PrimaryButton>
          </FormActions>
        </form>
      </CardContent>
    </Card>
  );
};

// Policy Actions Menu Component
const PolicyActionsMenu = ({ policy, onEdit, onDelete }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  
  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };
  
  const handleClose = () => {
    setAnchorEl(null);
  };

  return (
    <>
      <IconButton onClick={handleClick} size="small">
        <MoreVert />
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
      >
        <MenuItem onClick={() => { onEdit(policy); handleClose(); }}>
          <Edit sx={{ mr: 1 }} /> Edit Policy
        </MenuItem>
        <MenuItem onClick={() => { onDelete(policy); handleClose(); }} sx={{ color: 'error.main' }}>
          <Delete sx={{ mr: 1 }} /> Delete Policy
        </MenuItem>
      </Menu>
    </>
  );
};

// Responsive Mobile Policy Card
const MobilePolicyCard = ({ policy, onEdit, onDelete }) => {
  return (
    <Card sx={{ mb: 2 }}>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
          <Box sx={{ flex: 1 }}>
            <Typography variant="subtitle1" fontWeight={600}>
              {policy.insurance}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {policy.policy}
            </Typography>
          </Box>
          <PolicyActionsMenu
            policy={policy}
            onEdit={onEdit}
            onDelete={onDelete}
          />
        </Box>
      </CardContent>
    </Card>
  );
};

// Insurance Policies List Component with Search
const InsurancePoliciesList = ({ policies, companies, onEdit, onDelete }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Filter policies based on search term
  const filteredPolicies = policies.filter(policy =>
    policy.insurance.toLowerCase().includes(searchTerm.toLowerCase()) ||
    policy.policy.toLowerCase().includes(searchTerm.toLowerCase()) ||
    policy.id.toString().includes(searchTerm)
  );

  if (policies.length === 0) {
    return (
      <Alert severity="info">
        No insurance policies registered yet. Use the "Register Policy" tab to add your first policy.
      </Alert>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }}>
        <Typography variant="h5" sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>
          Insurance Policies ({policies.length})
        </Typography>
        
        <TextField
          size={isMobile ? "small" : "medium"}
          placeholder="Search policies..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          sx={{ minWidth: { xs: '100%', sm: 250 } }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search />
              </InputAdornment>
            ),
          }}
        />
      </Box>
      
      {filteredPolicies.length === 0 ? (
        <Alert severity="info">
          No policies found matching "{searchTerm}". Try adjusting your search terms.
        </Alert>
      ) : isMobile ? (
        // Mobile card layout
        <Box sx={{ mt: 2 }}>
          {filteredPolicies.map((policy) => (
            <MobilePolicyCard
              key={policy.id}
              policy={policy}
              onEdit={onEdit}
              onDelete={onDelete}
            />
          ))}
        </Box>
      ) : (
        // Desktop table layout
        <TableContainer component={Paper} sx={{ mt: 2 }}>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: 'primary.50' }}>
                <TableCell><strong>Insurance Company</strong></TableCell>
                <TableCell><strong>Policy Name</strong></TableCell>
                <TableCell><strong>Actions</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredPolicies.map((policy) => (
                <TableRow key={policy.id} hover>
                  <TableCell>
                    <Typography variant="body2" fontWeight={500}>
                      {policy.insurance}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {policy.policy}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <PolicyActionsMenu
                      policy={policy}
                      onEdit={onEdit}
                      onDelete={onDelete}
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};

// Company Policies View Component
const CompanyPoliciesView = ({ company, policies, onEdit, onDelete, onClose }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const companyPolicies = policies.filter(policy => policy.companyId === company.id);

  return (
    <Card>
      <CardHeader
        avatar={
          company.logo ? (
            <Avatar
              src={company.logo instanceof File ? URL.createObjectURL(company.logo) : company.logo}
              sx={{ width: { xs: 40, sm: 50 }, height: { xs: 40, sm: 50 } }}
              variant="rounded"
            />
          ) : (
            <Avatar sx={{ bgcolor: 'primary.main', width: { xs: 40, sm: 50 }, height: { xs: 40, sm: 50 } }}>
              <Business />
            </Avatar>
          )
        }
        title={
          <Typography variant={isMobile ? "h6" : "h5"}>
            {company.name} Policies
          </Typography>
        }
        subheader={`${companyPolicies.length} policies registered`}
        action={
          <IconButton onClick={onClose}>
            <Close />
          </IconButton>
        }
      />
      <CardContent>
        {companyPolicies.length === 0 ? (
          <Alert severity="info">
            No policies registered for this company yet.
          </Alert>
        ) : isMobile ? (
          // Mobile card layout
          <Box>
            {companyPolicies.map((policy) => (
              <MobilePolicyCard
                key={policy.id}
                policy={policy}
                onEdit={onEdit}
                onDelete={onDelete}
              />
            ))}
          </Box>
        ) : (
          // Desktop table layout
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: 'primary.50' }}>
                  <TableCell><strong>Insurance Company</strong></TableCell>
                <TableCell><strong>Policy Name</strong></TableCell>
                <TableCell><strong>Actions</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {companyPolicies.map((policy) => (
                  <TableRow key={policy.id} hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight={500}>
                        {policy.insurance}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {policy.policy}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <PolicyActionsMenu
                        policy={policy}
                        onEdit={onEdit}
                        onDelete={onDelete}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </CardContent>
    </Card>
  );
};

// Delete Confirmation Dialog
const DeleteConfirmationDialog = ({ open, onClose, onConfirm, title, content }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="sm" 
      fullWidth
      fullScreen={isMobile}
    >
      <DialogTitle sx={{ color: 'error.main' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Delete />
          {title}
        </Box>
      </DialogTitle>
      <DialogContent>
        <Typography>{content}</Typography>
      </DialogContent>
      <DialogActions sx={{ p: { xs: 2, sm: 1 } }}>
        <Button onClick={onClose} size={isMobile ? "small" : "medium"}>
          Cancel
        </Button>
        <Button 
          onClick={onConfirm} 
          color="error" 
          variant="contained"
          size={isMobile ? "small" : "medium"}
        >
          Delete
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// Main Insurance App Component
function Insurance() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  const [insuranceCompanies, setInsuranceCompanies] = useState([]);
  const [insurancePolicies, setInsurancePolicies] = useState([]);
  const [tabValue, setTabValue] = useState(0);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [editingCompany, setEditingCompany] = useState(null);
  const [editingPolicy, setEditingPolicy] = useState(null);
  const [viewingCompanyPolicies, setViewingCompanyPolicies] = useState(null);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, type: null, item: null });
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  // Update the handleCompanyRegistration function
  const handleCompanyRegistration = async (shouldRefresh) => {
    if (shouldRefresh) {
      await loadIns(); // Refresh the companies list
      
      // Reset editing state and switch to companies list tab
      setEditingCompany(null);
     // setTabValue(1); // Switch to Companies List tab
    }
  };

  // Update the handlePolicyRegistration function to reload policies
  const handlePolicyRegistration = async (shouldRefresh) => {
    if (shouldRefresh) {
      await loadpolicyv(); // Refresh the policies list
      
      // Reset editing state and switch to policies list tab
      setEditingPolicy(null);
      //setTabValue(2); // Switch to Policies List tab
    }
  };

  const handleCompanySelect = (company) => {
    setSelectedCompany(company);
    setTabValue(3); // Switch to policy registration tab
  };

  const handleEditCompany = (company) => {
    setEditingCompany(company);
    setTabValue(0); // Switch to company form tab
  };

  const handleEditPolicy = (policy) => {
    setEditingPolicy(policy);
    setTabValue(3); // Switch to policy form tab
  };

  const handleDeleteCompany = (company) => {
    setDeleteDialog({
      open: true,
      type: 'company',
      item: company,
    });
  };

  const handleDeletePolicy = (policy) => {
    setDeleteDialog({
      open: true,
      type: 'policy',
      item: policy,
    });
  };

  
  const handleConfirmDelete = async() => {
   
    const { type, item } = deleteDialog;
    
    if (type === 'company') {
        
        const response = await axios.delete(deleteInsurances(item.name));
        if (response.data.success) {
            successMessage('Delete',response.data.message)
           
             setInsuranceCompanies(prev => prev.filter(company => company.id !== item.id));
             setInsurancePolicies(prev => prev.filter(policy => policy.companyId !== item.id));
             setInsurancePolicies(prev => prev.filter(policy => policy.id !== item.id));
              
        }

     
    
      // Also delete all policies for this company
     
     // setSnackbar({ open: true, message: 'Company and its policies deleted successfully!', severity: 'success' });



    } else if (type === 'policy') {

        const response = await axios.delete(deletePolicy( item.id));
        if (response.data.success) {
            successMessage('Delete',response.data.message)
           
             setInsurancePolicies(prev => prev.filter(policy => policy.id !== item.id));
              
        }

          setInsurancePolicies(prev => prev.filter(policy => policy.id !== item.id));
     // setSnackbar({ open: true, message: 'Policy deleted successfully!', severity: 'success' });
   
    }
    
    setDeleteDialog({ open: false, type: null, item: null });
  };

  const handleViewCompanyPolicies = (company) => {
    setViewingCompanyPolicies(company);
  };

  const handleCancelEdit = () => {
    setEditingCompany(null);
    setEditingPolicy(null);
  };

  useEffect(() => {
    loadIns();
    loadpolicyv();
  }, []); 

  const loadIns = async () => {
    try {
      const response = await axios.get(loadInsurance);
      if (response.data.success) {
        setInsuranceCompanies(response.data.results); 
      }
    } catch (error) {
      await Swal.fire({
        title: 'Error',
        text: error.response?.data?.message || 'Failed to Load Insurance Companies',
        icon: 'error'
      });
    }
  };

  const loadpolicyv = async () => {
    try {
      const response = await axios.get(loadpolicy);
      if (response.data.success) {
        setInsurancePolicies(response.data.results); 
      }
    } catch (error) {
      await Swal.fire({
        title: 'Error',
        text: error.response?.data?.message || 'Failed to Load Policies',
        icon: 'error'
      });
    }
  };

  return (
    // No nested <ThemeProvider>/<CssBaseline> here any more. This screen sits
    // inside App's provider already, so re-providing the same theme did
    // nothing, and a second CssBaseline re-applies global resets partway down
    // the tree. The <AppBar> is gone for the same reason: the page it renders
    // into already has one, and stacking a second full-width bar cost a fifth
    // of a phone screen before any content appeared.
    <Box>
      <AdminPageHead
        icon={Business}
        title="Insurance Management System"
        subtitle="Companies, policies and the rules that apply to each"
      />

      {/* Side nav + working area. No maxWidth: the form was previously capped
          at 1280px inside a much wider panel, which is why three fields sat in
          a strip with the rest of the page empty. It now fills the space. */}
      <AdminWorkspace
        nav={
          <ResponsiveNavigation
            tabValue={tabValue}
            handleTabChange={handleTabChange}
            editingCompany={editingCompany}
            editingPolicy={editingPolicy}
            companies={insuranceCompanies}
            policies={insurancePolicies}
          />
        }
      >
        <TabPanel value={tabValue} index={0}>
          <InsuranceCompanyForm 
            onSubmit={handleCompanyRegistration} 
            editingCompany={editingCompany}
            onCancel={handleCancelEdit}
          />
        </TabPanel>

        <TabPanel value={tabValue} index={1}>
          {viewingCompanyPolicies ? (
            <CompanyPoliciesView
              company={viewingCompanyPolicies}
              policies={insurancePolicies}
              onEdit={handleEditPolicy}
              onDelete={handleDeletePolicy}
              onClose={() => setViewingCompanyPolicies(null)}
            />
          ) : (
            <InsuranceCompanyList 
              companies={insuranceCompanies} 
              onCompanySelect={handleCompanySelect}
              onEdit={handleEditCompany}
              onDelete={handleDeleteCompany}
              onViewPolicies={handleViewCompanyPolicies}
            />
          )}
        </TabPanel>

        <TabPanel value={tabValue} index={2}>
          <InsurancePoliciesList
            policies={insurancePolicies}
            companies={insuranceCompanies}
            onEdit={handleEditPolicy}
            onDelete={handleDeletePolicy}
          />
        </TabPanel>

        <TabPanel value={tabValue} index={3}>
          <InsurancePolicyForm 
            companies={insuranceCompanies}
            selectedCompany={selectedCompany}
            onSubmit={handlePolicyRegistration}
            editingPolicy={editingPolicy}
            onCancel={handleCancelEdit}
          />
        </TabPanel>
      </AdminWorkspace>

      {/* Delete Confirmation Dialog */}
      <DeleteConfirmationDialog
        open={deleteDialog.open}
        onClose={() => setDeleteDialog({ open: false, type: null, item: null })}
        onConfirm={handleConfirmDelete}
        title={deleteDialog.type === 'company' ? 'Delete Company' : 'Delete Policy'}
        content={
          deleteDialog.type === 'company'
            ? `Are you sure you want to delete "${deleteDialog.item?.name}"? This will also delete all associated policies.`
            : `Are you sure you want to delete the policy "${deleteDialog.item?.policy}"?`
        }
      />

      {/* Success/Error Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: isMobile ? 'center' : 'left' }}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default Insurance;